import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ResponsiveLayout } from '@/components/ResponsiveLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  DollarSign, Plus, Loader2, Clock, CheckCircle2, XCircle, Send,
  MoreVertical, Trash2, Filter, Ban, Banknote, ArrowRight, ShieldCheck,
  Paperclip, FileText, X, Upload
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { SectionHeader } from '@/components/SectionHeader';
import { MonthSelector, filterByMonth } from '@/components/shared/MonthSelector';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  pending: { label: 'Pending', icon: Clock, bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/20' },
  approved: { label: 'Approved', icon: CheckCircle2, bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/20' },
  disbursed: { label: 'Disbursed', icon: Banknote, bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/20' },
  rejected: { label: 'Rejected', icon: XCircle, bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/20' },
};

const purposeCategories = [
  { value: 'ads', label: 'Ads (FB/IG/TT/Google)' },
  { value: 'marketing_tools', label: 'Marketing Tools' },
  { value: 'content', label: 'Content Production' },
  { value: 'events', label: 'Events / Webinar' },
  { value: 'others', label: 'Others' },
];

export default function FundRequestPage() {
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [channels, setChannels] = useState([]);
  const [canApprove, setCanApprove] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [channelFilter, setChannelFilter] = useState('all');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [selYear, setSelYear] = useState(new Date().getFullYear());
  const [selMonth, setSelMonth] = useState(new Date().getMonth());
  const [newReq, setNewReq] = useState({
    request_for: 'channel',
    channel_id: '', channel_name: '',
    saas_project: '',
    podcast_show: '',
    purpose_category: 'ads', purpose_detail: '', amount: '', description: ''
  });
  const [uploadingFile, setUploadingFile] = useState(false);
  const [attachments, setAttachments] = useState([]);

  const token = localStorage.getItem('token');
  const h = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [reqRes, chRes, accessRes] = await Promise.all([
        axios.get(`${API}/finance/fund-requests`, h),
        axios.get(`${API}/ib/list`, h).catch(() => ({ data: [] })),
        axios.get(`${API}/finance/access-check`, h),
      ]);
      setRequests(reqRes.data.requests || []);
      setStats(reqRes.data.stats || {});
      setChannels(Array.isArray(chRes.data) ? chRes.data : []);
      setCanApprove(accessRes.data.can_approve);
    } catch (e) {
      if (e.response?.status === 403) toast.error('Akses ditolak. Modul Finance terhad.');
      else toast.error('Gagal memuatkan data');
    } finally { setLoading(false); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/finance/upload`, formData, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } });
      const f = res.data.file;
      setAttachments(prev => [...prev, { file_id: f.id, filename: f.original_filename, content_type: f.content_type }]);
      toast.success(`${file.name} uploaded`);
    } catch { toast.error('Upload gagal'); }
    finally { setUploadingFile(false); e.target.value = ''; }
  };

  const handleCreate = async () => {
    if (!newReq.amount || !newReq.purpose_category) { toast.error('Sila isi amount dan purpose'); return; }
    try {
      const ch = channels.find(c => c.id === newReq.channel_id);
      const res = await axios.post(`${API}/finance/fund-requests`, {
        ...newReq, amount: parseFloat(newReq.amount), channel_name: ch?.name || newReq.channel_id, attachments
      }, h);
      setRequests([res.data.request, ...requests]);
      setShowAddModal(false);
      setNewReq({ request_for: 'channel', channel_id: '', channel_name: '', saas_project: '', podcast_show: '', purpose_category: 'ads', purpose_detail: '', amount: '', description: '' });
      setAttachments([]);
      toast.success('Fund request submitted!');
      fetchData();
    } catch { toast.error('Gagal submit request'); }
  };

  const handleAction = async (requestId, action, reason = '') => {
    try {
      await axios.put(`${API}/finance/fund-requests/${requestId}`, { action, reason }, h);
      toast.success(`Request ${action === 'approve' ? 'approved' : action === 'reject' ? 'rejected' : 'disbursed'}!`);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this request?')) return;
    try { await axios.delete(`${API}/finance/fund-requests/${id}`, h); fetchData(); toast.success('Deleted'); } catch { toast.error('Failed'); }
  };

  const monthFiltered = filterByMonth(requests, selYear, selMonth, 'created_at');
  // Carry forward: pending/approved requests from previous months
  const carryForward = selMonth !== null ? requests.filter(r => {
    if (['disbursed', 'rejected'].includes(r.status)) return false;
    const d = new Date(r.created_at);
    return (d.getFullYear() === selYear && d.getMonth() < selMonth) || d.getFullYear() < selYear;
  }) : [];
  const visibleRequests = [...new Map([...monthFiltered, ...carryForward].map(r => [r.id, r])).values()];
  const filtered = visibleRequests.filter(r => {
    if (statusFilter !== 'all' && r.status !== statusFilter) return false;
    if (channelFilter !== 'all' && r.channel_id !== channelFilter) return false;
    return true;
  });

  if (loading) return <ResponsiveLayout><div className="flex-1 flex items-center justify-center min-h-screen bg-[hsl(var(--background))]"><Loader2 className="w-8 h-8 animate-spin text-[hsl(var(--primary))]" /></div></ResponsiveLayout>;

  return (
    <ResponsiveLayout>
      <div className="flex-1 min-h-screen bg-[hsl(var(--background))] text-[hsl(var(--text-primary))] p-4 lg:p-6">
        <SectionHeader icon={Send} title="Fund Request" subtitle="Submit & manage marketing fund requests"
          action={<Button onClick={() => setShowAddModal(true)} className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white" data-testid="new-fund-request-btn"><Plus size={18} className="mr-2" /> New Request</Button>}
        />

        {/* Month Selector */}
        <div className="mb-4">
          <MonthSelector year={selYear} month={selMonth} onChange={(y, m) => { setSelYear(y); setSelMonth(m); }} />
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
          {[
            { label: 'Total', value: visibleRequests.length, accent: '' },
            { label: 'Pending', value: visibleRequests.filter(r => r.status === 'pending').length, accent: 'border-amber-500/30', vc: 'text-amber-400' },
            { label: 'Approved', value: visibleRequests.filter(r => r.status === 'approved').length, accent: 'border-emerald-500/30', vc: 'text-emerald-400' },
            { label: 'Disbursed', value: visibleRequests.filter(r => r.status === 'disbursed').length, accent: 'border-blue-500/30', vc: 'text-blue-400' },
            { label: 'Total Approved', value: `RM ${visibleRequests.filter(r => ['approved','disbursed'].includes(r.status)).reduce((s,r) => s + (r.amount || 0), 0).toLocaleString()}`, accent: 'border-emerald-500/30', vc: 'text-emerald-400' },
          ].map(s => (
            <div key={s.label} className={`bg-[hsl(var(--card))] border ${s.accent || 'border-[hsl(var(--border))]'} rounded-xl p-4`}>
              <p className={`text-xs ${s.vc || 'text-[hsl(var(--text-muted))]'} mb-1`}>{s.label}</p>
              <p className={`text-xl font-bold ${s.vc || ''}`}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex gap-3 mb-3">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40 bg-[hsl(var(--surface))] border-[hsl(var(--border))]"><Filter size={14} className="mr-2" /><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="disbursed">Disbursed</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
          <Select value={channelFilter} onValueChange={setChannelFilter}>
            <SelectTrigger className="w-44 bg-[hsl(var(--surface))] border-[hsl(var(--border))]"><SelectValue placeholder="All Channels" /></SelectTrigger>
            <SelectContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
              <SelectItem value="all">All Channels</SelectItem>
              {channels.map(ch => <SelectItem key={ch.id} value={ch.id}>{ch.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Channel filter summary — total fund per filtered scope */}
        {channelFilter !== 'all' && (() => {
          // Scope follows currently selected month tab (visibleRequests already month-filtered)
          const scopedForChannel = visibleRequests.filter(r => r.channel_id === channelFilter);
          const filteredForChannel = filtered;
          const chanName = channels.find(c => c.id === channelFilter)?.name || channelFilter;
          const monthLabel = selMonth === null
            ? `All ${selYear}`
            : new Date(selYear, selMonth, 1).toLocaleString('en-US', { month: 'short', year: 'numeric' });
          const sumByStatus = (list, statuses) => list.filter(r => statuses.includes(r.status)).reduce((s, r) => s + (r.amount || 0), 0);
          const fmt = (v) => `RM ${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
          return (
            <div className="mb-6 bg-gradient-to-r from-[#10B981]/10 via-[hsl(var(--surface))] to-[#3B82F6]/10 border border-[#10B981]/30 rounded-xl p-4" data-testid="channel-filter-summary">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.15em] text-[#10B981] font-bold mb-1">{chanName} · {monthLabel}</p>
                  <p className="text-[11px] text-[hsl(var(--text-muted))]">Showing {filteredForChannel.length} request(s) after status filter · {scopedForChannel.length} total in {monthLabel}</p>
                </div>
                <div className="grid grid-cols-3 md:grid-cols-5 gap-4 flex-1 max-w-3xl">
                  <div>
                    <p className="text-[9px] uppercase text-[hsl(var(--text-muted))] mb-0.5">Total Requested</p>
                    <p className="text-base font-bold text-white tabular-nums">{fmt(scopedForChannel.reduce((s,r) => s + (r.amount || 0), 0))}</p>
                  </div>
                  <div>
                    <p className="text-[9px] uppercase text-amber-400 mb-0.5">Pending</p>
                    <p className="text-base font-bold text-amber-300 tabular-nums">{fmt(sumByStatus(scopedForChannel, ['pending']))}</p>
                  </div>
                  <div>
                    <p className="text-[9px] uppercase text-emerald-400 mb-0.5">Approved</p>
                    <p className="text-base font-bold text-emerald-300 tabular-nums">{fmt(sumByStatus(scopedForChannel, ['approved']))}</p>
                  </div>
                  <div>
                    <p className="text-[9px] uppercase text-blue-400 mb-0.5">Disbursed</p>
                    <p className="text-base font-bold text-blue-300 tabular-nums">{fmt(sumByStatus(scopedForChannel, ['disbursed']))}</p>
                  </div>
                  <div>
                    <p className="text-[9px] uppercase text-red-400 mb-0.5">Rejected</p>
                    <p className="text-base font-bold text-red-300 tabular-nums">{fmt(sumByStatus(scopedForChannel, ['rejected']))}</p>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Request List */}
        <div className="space-y-3">
          {filtered.length === 0 ? (
            <div className="text-center py-16 bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl">
              <Send size={48} className="mx-auto text-emerald-500/20 mb-4" />
              <p className="text-[hsl(var(--text-secondary))]">No fund requests</p>
            </div>
          ) : filtered.map(req => {
            const st = statusConfig[req.status] || statusConfig.pending;
            const StIcon = st.icon;
            return (
              <div key={req.id} className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl p-5 hover:border-emerald-500/20 transition-all" data-testid={`fund-req-${req.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3 flex-wrap">
                    <Badge className={`${st.bg} ${st.text} border ${st.border} text-xs`}><StIcon size={12} className="mr-1" />{st.label}</Badge>
                    <Badge className="bg-violet-500/10 text-violet-400 border border-violet-500/20 text-xs">{purposeCategories.find(p => p.value === req.purpose_category)?.label || req.purpose_category}</Badge>
                    {req.channel_name && <Badge className="bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 text-xs">{req.channel_name}</Badge>}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-xl font-bold text-emerald-400">RM {req.amount?.toLocaleString()}</span>
                    {canApprove && req.status === 'pending' && (
                      <>
                        <Button size="sm" onClick={() => handleAction(req.id, 'approve')} className="bg-emerald-500 text-white text-xs h-8"><CheckCircle2 size={12} className="mr-1" /> Approve</Button>
                        <Button size="sm" variant="outline" onClick={() => { setRejectTarget(req.id); setShowRejectModal(true); }} className="text-red-400 border-red-500/30 text-xs h-8"><XCircle size={12} className="mr-1" /> Reject</Button>
                      </>
                    )}
                    {canApprove && req.status === 'approved' && (
                      <Button size="sm" onClick={() => handleAction(req.id, 'disburse')} className="bg-blue-500 text-white text-xs h-8"><Banknote size={12} className="mr-1" /> Disbursed</Button>
                    )}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8"><MoreVertical size={14} /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                        <DropdownMenuItem onClick={() => handleDelete(req.id)} className="text-red-400"><Trash2 size={14} className="mr-2" /> Delete</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
                {req.purpose_detail && <p className="text-sm font-medium mb-1">{req.purpose_detail}</p>}
                {req.description && <p className="text-sm text-[hsl(var(--text-muted))] mb-3">{req.description}</p>}
                {req.attachments?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3" data-testid={`fund-req-attachments-${req.id}`}>
                    {req.attachments.map((a) => (
                      <a key={a.file_id} href={`${API}/finance/files/${a.file_id}?auth=${token}`} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-emerald-500/5 border border-emerald-500/15 hover:bg-emerald-500/10 transition-colors text-xs">
                        {a.content_type?.startsWith('image/') ? <Paperclip size={12} className="text-emerald-400" /> : <FileText size={12} className="text-emerald-400" />}
                        <span className="text-emerald-300 max-w-[150px] truncate">{a.filename}</span>
                      </a>
                    ))}
                  </div>
                )}
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-[hsl(var(--text-muted))] pt-3 border-t border-[hsl(var(--border))]">
                  <span>Requested by: <span className="text-[hsl(var(--text-secondary))] font-medium">{req.requester_name}</span></span>
                  {req.approved_by_name && <span><ArrowRight size={10} className="inline mx-1" />{req.status === 'rejected' ? 'Rejected' : 'Approved'} by: <span className="text-[hsl(var(--text-secondary))] font-medium">{req.approved_by_name}</span></span>}
                  <span>{new Date(req.created_at).toLocaleDateString('ms-MY', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                </div>
                {req.rejection_reason && <p className="text-xs text-red-400 mt-2 bg-red-500/5 p-2 rounded">Sebab ditolak: {req.rejection_reason}</p>}
              </div>
            );
          })}
        </div>

        {/* New Request Modal */}
        <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
          <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-md">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><Send size={18} className="text-emerald-400" /> New Fund Request</DialogTitle></DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm text-[hsl(var(--text-secondary))] mb-1.5 block font-medium">Request For *</label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { v: 'channel', l: 'Channel', emoji: '📺' },
                    { v: 'saas', l: 'SaaS Project', emoji: '💻' },
                    { v: 'podcast', l: 'Podcast', emoji: '🎙️' },
                  ].map(opt => (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => setNewReq({ ...newReq, request_for: opt.v, channel_id: '', channel_name: '', saas_project: '', podcast_show: '' })}
                      data-testid={`request-for-${opt.v}`}
                      className={`px-3 py-2.5 rounded-lg border text-xs font-semibold transition ${
                        newReq.request_for === opt.v
                          ? 'bg-gradient-to-br from-emerald-500/20 to-teal-500/10 border-emerald-500/50 text-emerald-300'
                          : 'bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-[hsl(var(--text-secondary))] hover:border-emerald-500/30'
                      }`}>
                      <div className="text-lg mb-0.5">{opt.emoji}</div>
                      {opt.l}
                    </button>
                  ))}
                </div>
              </div>

              {newReq.request_for === 'channel' && (
                <div>
                  <label className="text-sm text-[hsl(var(--text-secondary))] mb-1.5 block font-medium">Channel *</label>
                  <Select value={newReq.channel_id} onValueChange={(v) => setNewReq({ ...newReq, channel_id: v, channel_name: channels.find(c => c.id === v)?.name || v })}>
                    <SelectTrigger data-testid="channel-select" className="bg-[hsl(var(--surface))] border-[hsl(var(--border))]"><SelectValue placeholder="Select channel" /></SelectTrigger>
                    <SelectContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                      {channels.filter(c => c.status !== 'inactive').map(ch => <SelectItem key={ch.id} value={ch.id}>{ch.name}</SelectItem>)}
                      <SelectItem value="general">General / Company</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-[#6B7280] mt-1">Once disbursed, amount auto-reflect dalam Channel Budget page</p>
                </div>
              )}

              {newReq.request_for === 'saas' && (
                <div>
                  <label className="text-sm text-[hsl(var(--text-secondary))] mb-1.5 block font-medium">SaaS Project *</label>
                  <Input value={newReq.saas_project} onChange={(e) => setNewReq({ ...newReq, saas_project: e.target.value })}
                    placeholder="e.g. CRM Platform, Trading Bot..."
                    data-testid="saas-project-input"
                    className="bg-[hsl(var(--surface))] border-[hsl(var(--border))]" />
                </div>
              )}

              {newReq.request_for === 'podcast' && (
                <div>
                  <label className="text-sm text-[hsl(var(--text-secondary))] mb-1.5 block font-medium">Podcast Show *</label>
                  <Select value={newReq.podcast_show} onValueChange={(v) => setNewReq({ ...newReq, podcast_show: v })}>
                    <SelectTrigger data-testid="podcast-select" className="bg-[hsl(var(--surface))] border-[hsl(var(--border))]"><SelectValue placeholder="Select podcast" /></SelectTrigger>
                    <SelectContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                      <SelectItem value="Sembang Oh Yeah">Sembang Oh Yeah</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-[hsl(var(--text-secondary))] mb-1.5 block font-medium">Purpose Category *</label>
                  <Select value={newReq.purpose_category} onValueChange={(v) => setNewReq({ ...newReq, purpose_category: v })}>
                    <SelectTrigger className="bg-[hsl(var(--surface))] border-[hsl(var(--border))]"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                      {purposeCategories.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm text-[hsl(var(--text-secondary))] mb-1.5 block font-medium">Amount (RM) *</label>
                  <Input type="number" value={newReq.amount} onChange={(e) => setNewReq({ ...newReq, amount: e.target.value })} placeholder="0.00" className="bg-[hsl(var(--surface))] border-[hsl(var(--border))]" />
                </div>
              </div>
              <div>
                <label className="text-sm text-[hsl(var(--text-secondary))] mb-1.5 block font-medium">Purpose Detail</label>
                <Input value={newReq.purpose_detail} onChange={(e) => setNewReq({ ...newReq, purpose_detail: e.target.value })} placeholder="e.g. GetResponse subscription, Heygen AI credits..." className="bg-[hsl(var(--surface))] border-[hsl(var(--border))]" />
              </div>
              <div>
                <label className="text-sm text-[hsl(var(--text-secondary))] mb-1.5 block font-medium">Description / Notes</label>
                <textarea value={newReq.description} onChange={(e) => setNewReq({ ...newReq, description: e.target.value })} placeholder="Additional details..." rows={3} className="w-full bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-md p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500/30" />
              </div>
              {/* File Upload */}
              <div>
                <label className="text-sm text-[hsl(var(--text-secondary))] mb-1.5 block font-medium">Attachments (Receipt / Invoice)</label>
                {attachments.length > 0 && (
                  <div className="space-y-1 mb-2">
                    {attachments.map((a, i) => (
                      <div key={a.file_id} className="flex items-center gap-2 p-2 rounded-lg bg-[hsl(var(--surface))] border border-[hsl(var(--border))]">
                        <FileText size={14} className="text-emerald-400 shrink-0" />
                        <span className="text-xs text-white truncate flex-1">{a.filename}</span>
                        <button onClick={() => setAttachments(attachments.filter((_, j) => j !== i))} className="text-[#6b7280] hover:text-red-400 shrink-0"><X size={12} /></button>
                      </div>
                    ))}
                  </div>
                )}
                <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-[hsl(var(--border))] hover:border-emerald-500/50 cursor-pointer transition-colors">
                  {uploadingFile ? <Loader2 size={14} className="animate-spin text-emerald-400" /> : <Upload size={14} className="text-[hsl(var(--text-muted))]" />}
                  <span className="text-xs text-[hsl(var(--text-muted))]">{uploadingFile ? 'Uploading...' : 'Click to upload file'}</span>
                  <input type="file" className="hidden" onChange={handleFileUpload} disabled={uploadingFile} />
                </label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddModal(false)}>Cancel</Button>
              <Button onClick={handleCreate} className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white">Submit Request</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reject Modal */}
        <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
          <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-sm">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><Ban size={18} className="text-red-400" /> Reject Request</DialogTitle></DialogHeader>
            <div className="py-4">
              <label className="text-sm text-[hsl(var(--text-secondary))] mb-1.5 block font-medium">Reason for rejection</label>
              <textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="State reason..." rows={3} className="w-full bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-md p-3 text-sm resize-none" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRejectModal(false)}>Cancel</Button>
              <Button onClick={() => { handleAction(rejectTarget, 'reject', rejectReason); setShowRejectModal(false); setRejectReason(''); }} className="bg-red-500 text-white">Reject</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </ResponsiveLayout>
  );
}
